import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import usePanelFlotante from "./usePanelFlotante";
import "./MultiSelect.css";

/**
 * Selector de varios valores, con busqueda.
 *
 * Es hermano de SearchSelect y comparte con el el hook de posicionamiento.
 * La diferencia real no es el aspecto sino el comportamiento: al elegir una
 * opcion el panel NO se cierra, porque lo normal es marcar dos o tres
 * seguidas y cerrar cada vez obligaria a reabrir.
 *
 * El valor es un array de ids. Vacio = sin filtrar, que no es lo mismo que
 * "ninguno": por eso el texto del control dice "All ..." cuando esta vacio.
 *
 * Props:
 *   valores      array de ids seleccionados
 *   onChange     recibe el array nuevo
 *   options      [{ value, label }]
 *   textoVacio   lo que se muestra sin seleccion ("All categories")
 *   nombre       como se llama un elemento en plural ("categories")
 */
function MultiSelect({
  valores = [],
  onChange,
  options = [],
  textoVacio = "All",
  nombre = "options",
  buscarPlaceholder = "Type to search...",
  altoLista = 240,
  disabled = false,
}) {
  const { abierto, setAbierto, triggerRef, panelRef, pos } = usePanelFlotante({
    altoLista,
    extra: 96, // buscador + barra de acciones
  });

  const [q, setQ] = useState("");

  useEffect(() => {
    if (!abierto) setQ("");
  }, [abierto]);

  // Set de strings: los ids pueden llegar como number de la base y como
  // string de un <option>, y "3" !== 3 arruinaria la comparacion.
  const elegidos = useMemo(
    () => new Set((valores || []).map((v) => String(v))),
    [valores]
  );

  const filtradas = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return options;
    return options.filter((o) =>
      String(o.label || "").toLowerCase().includes(query)
    );
  }, [options, q]);

  const alternar = (valor) => {
    const v = String(valor);
    const siguiente = new Set(elegidos);
    if (siguiente.has(v)) siguiente.delete(v);
    else siguiente.add(v);
    onChange([...siguiente]);
  };

  /**
   * Texto del control.
   *
   * Con uno elegido se muestra su nombre, que es mas util que "1 selected".
   * De dos en adelante el nombre completo no cabe, asi que se cuenta.
   */
  const texto = useMemo(() => {
    if (elegidos.size === 0) return textoVacio;

    if (elegidos.size === 1) {
      const uno = options.find((o) => elegidos.has(String(o.value)));
      return uno ? uno.label : `1 ${nombre}`;
    }

    return `${elegidos.size} ${nombre}`;
  }, [elegidos, options, textoVacio, nombre]);

  const limpiar = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const panel =
    abierto && !disabled && pos
      ? createPortal(
          <div
            ref={panelRef}
            className="ms-panel"
            style={{
              left: pos.left,
              width: Math.max(pos.width, 230),
              ...(pos.top !== null ? { top: pos.top } : { bottom: pos.bottom }),
              maxHeight: pos.maxAlto,
            }}
          >
            <div className="ms-buscador">
              <input
                autoFocus
                className="ms-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={buscarPlaceholder}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setAbierto(false);
                  }
                }}
              />
            </div>

            <div className="ms-lista" style={{ maxHeight: altoLista }}>
              {filtradas.length === 0 ? (
                <p className="ms-vacio">No matches</p>
              ) : (
                filtradas.map((o) => {
                  const marcado = elegidos.has(String(o.value));
                  return (
                    <button
                      type="button"
                      key={o.value}
                      className={`ms-opcion ${marcado ? "ms-opcion--on" : ""}`}
                      onClick={() => alternar(o.value)}
                      role="checkbox"
                      aria-checked={marcado}
                    >
                      <span className="ms-caja" aria-hidden="true">
                        {marcado ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12.5l4.5 4.5L19 7" />
                          </svg>
                        ) : null}
                      </span>

                      <span className="ms-texto">{o.label}</span>
                    </button>
                  );
                })
              )}
            </div>

            <div className="ms-pie">
              <span className="ms-cuenta">
                {elegidos.size > 0
                  ? `${elegidos.size} of ${options.length} selected`
                  : `${options.length} ${nombre}`}
              </span>

              {elegidos.size > 0 ? (
                <button
                  type="button"
                  className="ms-limpiar"
                  onClick={() => onChange([])}
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className={`ms ${disabled ? "ms--off" : ""}`}>
      <button
        type="button"
        ref={triggerRef}
        className={`ms-control ${abierto ? "ms-control--abierto" : ""} ${
          elegidos.size > 0 ? "ms-control--con-valor" : ""
        }`}
        onClick={() => !disabled && setAbierto((v) => !v)}
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={abierto}
      >
        <span className="ms-valor">{texto}</span>

        {elegidos.size > 0 ? (
          <span
            className="ms-x"
            role="button"
            tabIndex={-1}
            aria-label={`Clear ${nombre}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={limpiar}
          >
            ✕
          </span>
        ) : null}

        <span className="ms-flecha" aria-hidden="true">
          ▾
        </span>
      </button>

      {panel}
    </div>
  );
}

export default MultiSelect;
